// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xHasRole } from "@synd/App/Store/Selectors/GeneralSelectors";
import { xGetFulfillmentLabel } from "@synd/App/Store/Selectors/HeaderShipToAddressSelector";
import Tooltip, { TooltipPresentationProps } from "@synd/MobiusOverrides/Tooltip/Tooltip";
import { USER_ROLES } from "@synd/XDL/constants";
import HeaderUtilityBarVerticalDivider from "@synd/XDL/Icons/HeaderUtilityBarVerticalDivider";
import HeaderUtilitySwitchAccount from "@synd/XDL/Icons/HeaderUtilitySwitchAccount";
import HeaderUtilitySwitchWholesaler from "@synd/XDL/Icons/HeaderUtilitySwitchWholesaler";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    // XNG-Changes: Change to xGetFulfillmentLabel
    fulfillmentLabel: xGetFulfillmentLabel(state),
    isDrawerOpen: state.components.addressDrawer.isOpen,
    isPunchOutSession: getIsPunchOutSession(state),
    // XNG-Changes:
    isRetailer: xHasRole(state, USER_ROLES.RETAILER),
});

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    // XNG-Changes:
    fulfillmentAddressValue?: TypographyPresentationProps;
    fulfillmentSeparator?: IconPresentationProps;
    clickableText?: TypographyPresentationProps;
    clickableIcon?: IconPresentationProps;
    shipToToolTip?: TooltipPresentationProps;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
            width: calc(100% - 10px);
            padding: 6px 0;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderShipToAddress: FC<Props> = ({
    id,
    fulfillmentLabel,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    // XNG-Changes:
    isRetailer,
}) => {
    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };

    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper}>
                {/* As this doesn't look final, but is the pattern on which the mobile nav drawer was based,
                    please also make changes to `NavigationDrawer.tsx` when you change this. */}
                {/* XNG-Changes: Commented icon */}
                {/* <Icon {...styles.titleIcon} src={MapPin} /> */}
                {/* <XNG-Changes> */}
                <Typography {...styles.titleTypography} data-test-selector="header_shipToLabel">
                    {fulfillmentLabel.addressLabel}
                </Typography>
                <Tooltip
                    triggerComponent={
                        <Typography {...styles.fulfillmentAddressValue}>{fulfillmentLabel.addressValue}</Typography>
                    }
                    text={fulfillmentLabel.fullAddressValue}
                    {...styles.shipToToolTip}
                />
                <Icon src={HeaderUtilityBarVerticalDivider} {...styles.fulfillmentSeparator}></Icon>
                <Clickable
                    {...styles.titleClickable}
                    onClick={handleClickAddress}
                    data-test-selector="header_shipToButton"
                >
                    <Icon
                        {...styles.clickableIcon}
                        src={isRetailer ? HeaderUtilitySwitchWholesaler : HeaderUtilitySwitchAccount}
                    ></Icon>
                    <Typography {...styles.clickableText}>
                        {translate(`Switch ${isRetailer ? "WholeSaler" : "Account"}`)}
                    </Typography>
                </Clickable>
                {/* </XNG-Changes> */}
            </StyledWrapper>
            <Drawer
                {...drawerStyles}
                position="top"
                isOpen={isDrawerOpen}
                headline={isPunchOutSession ? "" : translate("Change Customer")}
                handleClose={handleCloseDrawer}
                data-test-selector="addressDrawer"
            >
                {createWidgetElement("Header/AddressDrawerContainer", {
                    fields: {},
                })}
                {/* <Zone zoneName="Container" contentId={id} /> */}
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
